/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ChannelReference } from '../models/ChannelReference';
import type { ConnectionKey } from '../models/ConnectionKey';
import type { DeviceResponse } from '../models/DeviceResponse';
import type { HspAdd } from '../models/HspAdd';
import type { HspState } from '../models/HspState';
import type { PauseOnStarving } from '../models/PauseOnStarving';
import type { PlaybackRate } from '../models/PlaybackRate';
import type { ServerTimeEstimate } from '../models/ServerTimeEstimate';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class HspService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Setup a new HSP session on the device.
     * Setup initializes a new HSP session on the device. This ensures both the device and server are properly prepared, clearing any existing HSP session state.
     *
     * If the device is already in an HSP session and no setup command is issued, all HSP commands will modify the existing session, which may lead to unexpected behavior.
     *
     * The `stream_id` is an optional session identifier. If it changes during a HSP session, it indicates that a new session has been initiated by a client. If no `stream_id` is provided, one will be generated and returned in the setup response.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hsp">Handy Streaming Protocol (HSP)</a> for additional information about the HSP protocol and code samples.
     *
     * @param xConnectionKey A device connection key or a channel reference.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @param requestBody HSP setup
     * @returns any HSP state.
     * @throws ApiError
     */
    public hspSetup(
        xConnectionKey: (ConnectionKey | ChannelReference),
        timeout: number = 5000,
        requestBody?: {
            /**
             * The stream id.
             */
            stream_id?: number;
        },
    ): CancelablePromise<(DeviceResponse & {
        result?: HspState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hsp/setup',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Flush the HSP buffer.
     * Flush will remove all existing points from the device point buffer.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hsp">Handy Streaming Protocol (HSP)</a> for additional information about the HSP protocol and code samples.
     *
     * @param xConnectionKey Device connection key.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HSP state.
     * @throws ApiError
     */
    public hspFlush(
        xConnectionKey: ConnectionKey,
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HspState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hsp/flush',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
        });
    }

    /**
     * Add points to the device HSP point buffer.
     * You can add up to 100 points to the device's point buffer in a single command.
     *
     * If the buffer is full, adding N points will remove the first N points to make room for the new ones.
     *
     * The `flush` flag can be used to remove all existing points from the buffer before adding the new points.
     *
     * The `tail_point_threshold` parameter can be used to update the tail point stream index threshold after the points have been added.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hsp">Handy Streaming Protocol (HSP)</a> for additional information about the HSP protocol and code samples.
     *
     * @param xConnectionKey Device connection key.
     * @param requestBody HSP point
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HSSP state.
     * @throws ApiError
     */
    public hspAdd(
        xConnectionKey: ConnectionKey,
        requestBody: HspAdd,
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HspState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hsp/add',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Start the HSP playback.
     * Start the HSP playback.
     *
     * An optional add points command can be embedded in the play command. The add command (if present) will be executed before the playback starts.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hsp">Handy Streaming Protocol (HSP)</a> for additional information about the HSP protocol and code samples.
     *
     * @param xConnectionKey Device connection key.
     * @param requestBody HSP play
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HSSP state.
     * @throws ApiError
     */
    public hspPlay(
        xConnectionKey: ConnectionKey,
        requestBody: {
            /**
             * The start time in milliseconds.
             */
            start_time: number;
            server_time?: ServerTimeEstimate;
            playback_rate?: PlaybackRate;
            pause_on_starving?: PauseOnStarving;
            /**
             * The loop state.
             */
            loop?: boolean;
            add?: HspAdd;
        },
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HspState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hsp/play',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Stop the HSP playback.
     * Stop the HSP playback.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hsp">Handy Streaming Protocol (HSP)</a> for additional information about the HSP protocol and code samples.
     *
     * @param xConnectionKey Device connection key.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HSSP state.
     * @throws ApiError
     */
    public hspStop(
        xConnectionKey: ConnectionKey,
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HspState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hsp/stop',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
        });
    }

    /**
     * Get the current HSP state of the device.
     * Get the current HSP state of the device.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hsp">Handy Streaming Protocol (HSP)</a> for additional information about the HSP protocol and code samples.
     *
     * @param xConnectionKey Device connection key.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HSP state.
     * @throws ApiError
     */
    public getHspState(
        xConnectionKey: ConnectionKey,
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HspState;
    })> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/hsp/state',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
        });
    }

    /**
     * Set the HSP tail point stream index threshold.
     * Set the HSP tail point stream index threshold.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hsp">Handy Streaming Protocol (HSP)</a> for additional information about the HSP protocol and code samples.
     *
     * @param xConnectionKey Device connection key.
     * @param requestBody HSP threshold
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HSP state.
     * @throws ApiError
     */
    public setHspThreshold(
        xConnectionKey: ConnectionKey,
        requestBody: {
            /**
             * The HSP tail point stream index threshold.
             */
            tail_point_threshold: number;
        },
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HspState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hsp/threshold',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Pause the HSP playback.
     * Pause the HSP playback.
     *
     * Pause will pause the playback, but keep the current position. A subsequent resume command will continue playback from the paused position or from the current 'live' stream position, depending on the resume command parameters.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hsp">Handy Streaming Protocol (HSP)</a> for additional information about the HSP protocol and code samples.
     *
     * @param xConnectionKey Device connection key.
     * @returns HspState HSP state.
     * @throws ApiError
     */
    public hspPause(
        xConnectionKey: ConnectionKey,
    ): CancelablePromise<HspState> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hsp/pause',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
        });
    }

    /**
     * Resume the HSP playback.
     * Resume the HSP playback.
     *
     * Depending on the `pick_up` parameter, resume will either continue from the paused position (`pick_up` = false) or jump to the current 'live' position of the stream (`pick_up` = true).
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hsp">Handy Streaming Protocol (HSP)</a> for additional information about the HSP protocol and code samples.
     *
     * @param xConnectionKey Device connection key.
     * @param requestBody HSP resume
     * @returns HspState HSP state.
     * @throws ApiError
     */
    public hspResume(
        xConnectionKey: ConnectionKey,
        requestBody: {
            /**
             * If `true`, the playback will resume from the current 'live' position of the stream. If `false`, it will resume from the paused position.
             */
            pick_up?: boolean;
        },
    ): CancelablePromise<HspState> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hsp/resume',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Set the HSP pause-on-starving flag.
     * Set the HSP pause-on-starving flag.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hsp">Handy Streaming Protocol (HSP)</a> for additional information about the HSP protocol and code samples.
     *
     * @param xConnectionKey Device connection key.
     * @param requestBody HSP pause-on-starving
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HSP state.
     * @throws ApiError
     */
    public setHspPauseOnStarving(
        xConnectionKey: ConnectionKey,
        requestBody: {
            /**
             * The HSP pause-on-starving flag.
             */
            pause_on_starving: boolean;
        },
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HspState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hsp/pause/onstarving',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Adjust the stream synchronization.
     * Adjust the stream playtime using the provided current time sample from the external source and filter.
     *
     * This can improve the synchronization between the device and the external source when the current time samples have some variable inaccuracies.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hsp">Handy Streaming Protocol (HSP)</a> for additional information about the HSP protocol and code samples.
     *
     * @param xConnectionKey Device connection key.
     * @param requestBody HSP time
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HSP state.
     * @throws ApiError
     */
    public setHspTime(
        xConnectionKey: ConnectionKey,
        requestBody: {
            /**
             * The time.
             */
            current_time: number;
            server_time: ServerTimeEstimate;
            /**
             * The filter to use when setting the time.
             */
            filter?: number;
        },
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HspState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hsp/synctime',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Set the HSP loop flag.
     * Set the HSP loop flag.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hsp">Handy Streaming Protocol (HSP)</a> for additional information about the HSP protocol and code samples.
     *
     * @param xConnectionKey Device connection key.
     * @param requestBody HSP loop
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HSP state.
     * @throws ApiError
     */
    public setHspLoop(
        xConnectionKey: ConnectionKey,
        requestBody: {
            /**
             * The HSP loop flag.
             */
            loop: boolean;
        },
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HspState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hsp/loop',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * Set the HSP playback rate.
     * Set the HSP playback rate.
     *
     * See the request body schema definition for details.<br><br>See <a href="https://links.handyfeeling.com/docs-api-handy-rest-v3-hsp">Handy Streaming Protocol (HSP)</a> for additional information about the HSP protocol and code samples.
     *
     * @param xConnectionKey Device connection key.
     * @param requestBody HSP playback rate
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @returns any HSP state.
     * @throws ApiError
     */
    public setHspPaybackRate(
        xConnectionKey: ConnectionKey,
        requestBody: {
            /**
             * The HSP playback_rate
             */
            playback_rate: number;
        },
        timeout: number = 5000,
    ): CancelablePromise<(DeviceResponse & {
        result?: HspState;
    })> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/hsp/playbackrate',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

}
